import React from 'react';
import { Link } from 'react-router-dom';
import { Flame } from 'lucide-react';
import {
  friendInterestText, CyclingEvent, EVENT_STATUS_LABEL, EVENT_STATUS_TEXT, EVENT_STATUS_TEXT_DARK, POPULAR_LABEL, eventCardModel,
} from '@radevents/shared';

/**
 * Content-Bereich der Event-Card unter dem schlanken Bild-Header (Design „kompakte Event-Cards"):
 * Meta-Zeile (Zustand als Wort vor dem kategoriefarbenen Kategorie·Eventart-Label, dazu Anmeldestand
 * und „Beliebt"), Titel,
 * Ort-Zeile (Stadt · Bundesland, bei aktiver Standortsuche „ca. X km entfernt") und die
 * Distanz-Chips. Datum und Countdown stehen bewusst im Bild-Header (EventCardHero), nicht hier.
 * Nur bei einer gebuchten Card bleibt der Countdown in der Ortszeile, weil im Kopf neben Herz und
 * Kennzeichnung kein Platz mehr ist.
 *
 * Gemeinsam genutzt von Übersicht (Landing) und Merkliste (Favorites). Web/App-Unterschiede
 * (Padding, Titelgröße) steuert die CSS-Klasse `event-card-body` responsiv — mobil = App-Ansicht.
 *
 * **Abgeleitet wird nichts mehr hier**, sondern in `eventCardModel` (`shared/`): Beschriftungen,
 * Farben, Status, Region, Entfernung und Chips zeichnet die App aus demselben Modell.
 * Der frühere Prop `statusLabel` ist entfallen — alle vier Aufrufstellen haben dieselbe Regel
 * abgeschrieben, das war die fünfte Kopie.
 */

interface Props {
  event: CyclingEvent;
  /** Ursprung der Standortsuche — nur dann wird „ca. X km entfernt" gezeigt. */
  userLat?: number | null;
  userLng?: number | null;
  /**
   * Ziel der Detailseite. Gesetzt = der Titel wird zum echten Link, der sich unsichtbar über die
   * ganze Card spannt (`.event-card-link::after`, siehe App.css). Nur so öffnen Mausrad-Klick,
   * Strg-/Cmd-Klick und „Link in neuem Tab öffnen" einen zweiten Tab — ein onClick-Handler auf
   * der Card kann das nicht, weil der Browser beim Mausrad gar keinen Klick meldet.
   */
  to?: string;
  /** Läuft zusätzlich beim normalen Linkklick (z. B. Listen-/Scrollzustand sichern). */
  onNavigate?: (e: React.MouseEvent) => void;
}

const EventCardBody: React.FC<Props> = ({ event, userLat, userLng, to, onNavigate }) => {
  const m = eventCardModel(event, { userLat, userLng });
  // Normale Card: Countdown im Kopf neben dem Herz (EventCardHero). Gebuchte Card: hier.
  const countdown = m.countdownSlot === 'region' ? m.countdown : null;

  return (
    <div className={`event-card-body${m.cancelled ? ' event-card-body--abgesagt' : ''}`}>
      <div className="ecb-meta">
        <span className="ecb-cat" style={{ color: m.categoryColor }}>
          <span className="ecb-diamond" style={{ background: m.categoryColor }} />
          {/* Zustand vorn im Kicker wie in der Event-Zeile: ein Wort in seiner Farbe statt einer
              vollroten Pille. Nur die Absage ist eine Warnung (EVENT_STATUS_TEXT). */}
          {m.status && <>
            <span className="ecb-status" style={{ color: (m.inverted ? EVENT_STATUS_TEXT_DARK : EVENT_STATUS_TEXT)[m.status] }}>
              {EVENT_STATUS_LABEL[m.status]}
            </span>
            {' · '}
          </>}
          {m.categoryLabel}
        </span>
        {m.registration && (
          // Satzschreibung statt Versalien und ein Punkt statt Rahmen-Sperrsatz: auf Chip-Größe
          // ist das der Unterschied zwischen lesen und entziffern. Die Farben stehen als Klasse
          // in App.css, damit die invertierte Card (Hervorhebung 1c) sie überschreiben kann.
          <span className={`ecb-reg ecb-reg--${m.registration.tone === 'positive' ? 'open' : 'muted'}`}
            title={m.registration.long}>
            <span className="ecb-reg-dot" />
            {m.registration.label}
          </span>
        )}
        {m.popular && (
          // Leise, in Marke: eine Auskunft wie der Anmeldestand, keine Warnung. Auf gebuchten
          // Cards liefert das Modell `false` (dort steht schon die Pflicht-Kennzeichnung).
          <span className="ecb-popular">
            <Flame size={12} aria-hidden />
            {POPULAR_LABEL}
          </span>
        )}
      </div>

      <h3 className="event-card-title" style={m.cancelled ? { textDecoration: 'line-through' } : undefined}>
        {to
          ? <Link className="event-card-link" to={to} onClick={onNavigate}>{event.title}</Link>
          : event.title}
      </h3>

      {(m.region || countdown || m.distanceKm != null) && (
        <div className="ecb-region">
          {m.region}
          {m.distanceKm != null && <span className="ecb-dist">{m.region ? ' · ' : ''}ca. {m.distanceKm} km entfernt</span>}
          {countdown && (
            <span className="ecb-countdown">
              {m.region || m.distanceKm != null ? <span className="ecb-sep"> | </span> : ''}
              {countdown}
            </span>
          )}
        </div>
      )}


      {(m.chips.length > 0 || !!event.friendInterests?.length) && <div className="ecb-footer">
      {m.chips.length > 0 && (
        <div className="ecb-chips">
          {m.chips.map((c) => <span key={c} className="ecb-chip">{c}</span>)}
          {m.more && <span className="ecb-chip ecb-chip-more">{m.more}</span>}
        </div>
      )}
      {!!event.friendInterests?.length && <div className="community-friends">{friendInterestText(event.friendInterests)}</div>}
      </div>}
    </div>
  );
};

export default EventCardBody;
